import {
  GetReviewResponse,
  GetReviewResponseWithPermissions,
  ProductReviewFilterValues,
  ProductReviewsDataMapper,
  ReviewsData,
} from '@lib/types/api/Review';
import { ReviewFormData } from '@ui-components/index';
import { useCallback, useState } from 'react';
import {
  DELETE_REFETCH_ATTEMPTS,
  DELETE_REFETCH_RETRY_DELAY_MS,
  PAGE_SIZE,
} from '../constants/reviews';
import { productReviewsDataMapper } from '../mappers/productReviewsDataMapper';

export type ReviewsSnapshot = {
  total?: number;
  reviews?: { reviewDetails: { id: string } }[];
};

export type UseReviewsParams = {
  productId: string;
  publisherSupportContact?: string;
  isMultiPackage?: boolean;
  /**
   * The server-rendered reviews already on screen. Nothing fetches on mount, so
   * without this the hook has no baseline to compare a post-delete refetch
   * against until the user pages, filters or sorts.
   */
  initialReviewsData?: ReviewsSnapshot;
};

const wait = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Creates an optimistic review structure from POST response data.
 */
export const createOptimisticReview = (
  responseData: GetReviewResponseWithPermissions,
  productId: string,
  isMultiPackage: boolean
): ProductReviewsDataMapper => {
  return productReviewsDataMapper(responseData, {
    includeWriteReviewModal: true,
    productId,
    isMultiPackage,
    canSubmitReview: true,
  });
};

/**
 * Updates an existing review with new data from PATCH response.
 */
export const updateOptimisticReview = (
  existingReview: ProductReviewsDataMapper,
  patchResponse: GetReviewResponse,
  productId: string,
  isMultiPackage: boolean
): ProductReviewsDataMapper => {
  const mergedReview: GetReviewResponseWithPermissions = {
    ...patchResponse,
    userProfile: existingReview.reviewerInfo
      ? {
          id: existingReview.reviewerInfo.link?.split('/').pop() || '',
          username: existingReview.reviewerInfo.username,
          name: existingReview.reviewerInfo.username, // Not currently used
          image: existingReview.reviewerInfo.avatarUrl || '',
        }
      : {
          id: '',
          username: '',
          name: '',
          image: '',
        },
    permissions: {
      canEdit: existingReview.reviewDetails.canEdit,
      canDelete: existingReview.reviewDetails.canDelete,
      canReportReview: existingReview.reviewDetails.canReportReview,
      canMarkHelpful: false,
      canUnmarkHelpful: false,
      canMarkUnhelpful: false,
      canUnmarkUnhelpful: false,
      canReportReply: false,
    },
    reply: existingReview.reviewDetails.reply
      ? {
          id: existingReview.reviewDetails.reply.id,
          reviewId: existingReview.reviewDetails.id,
          publisherId: '',
          body: existingReview.reviewDetails.reply.body,
          createdAt: existingReview.reviewDetails.reply.publishedDate,
          updatedAt:
            existingReview.reviewDetails.reply.editedDate ||
            existingReview.reviewDetails.reply.publishedDate,
        }
      : undefined,
  };

  return productReviewsDataMapper(mergedReview, {
    includeWriteReviewModal: true,
    productId,
    isMultiPackage,
    canSubmitReview: existingReview.reviewDetails.canSubmitReview,
  });
};

export const useReviews = ({
  productId,
  publisherSupportContact,
  isMultiPackage = false,
  initialReviewsData,
}: UseReviewsParams) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewsData, setReviewsData] = useState<ReviewsData | undefined>(
    undefined
  );
  const [activeFilter, setActiveFilter] = useState<ProductReviewFilterValues>();
  const [activeSort, setActiveSort] = useState<string>('recent');

  const getReviews = useCallback(
    async (page: number, filter?: string, sort?: string) => {
      if (!productId) return;

      setLoading(true);
      setError(null);

      try {
        const query = new URLSearchParams({
          page: page.toString(),
          ...(filter && { rating: filter }),
          ...(sort && { sort }),
          // Add timestamp to prevent caching
          _t: Date.now().toString(),
        });

        const res = await fetch(`/api/products/${productId}/reviews?${query}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0',
          },
        });
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || 'Failed to fetch');
        }

        setReviewsData(data);
        return data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [productId]
  );

  // Review Actions
  const onFilter = useCallback(
    async (filter: string) => {
      const stars = Number(filter);
      const filterValue = (stars === 0
        ? ''
        : filter) as unknown as ProductReviewFilterValues;

      setActiveFilter(filterValue === '' ? undefined : filterValue);

      const reviewsData = await getReviews(
        1,
        filterValue === '' ? undefined : filterValue,
        activeSort
      );

      return reviewsData;
    },
    [getReviews, activeSort]
  );

  const onSort = useCallback(
    async (sort: string) => {
      setActiveSort(sort);
      const reviewsData = await getReviews(1, activeFilter, sort);
      return reviewsData;
    },
    [getReviews, activeFilter]
  );

  const onPageChange = useCallback(
    async (page: number) => {
      return await getReviews(page, activeFilter, activeSort);
    },
    [getReviews, activeFilter, activeSort]
  );

  const onSubmitReport = useCallback(
    async (
      reviewId: string,
      productId: string,
      report: { reportReview: string; type: string }
    ) => {
      try {
        const url = new URL(
          `/api/products/${productId}/reviews/${reviewId}/report`,
          window.location.origin
        );

        const res = await fetch(url.toString(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            reason: report.reportReview,
            type: report.type,
          }),
        });

        if (!res.ok) throw new Error('Error reporting review');
      } catch (error) {
        console.error('Report failed:', error);
        throw error;
      }
    },
    []
  );

  const onSubmitReview = useCallback(
    async (productId: string, formData: ReviewFormData) => {
      try {
        const url = new URL(
          `/api/products/${productId}/reviews`,
          window.location.origin
        );

        const res = await fetch(url.toString(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });

        if (!res.ok) {
          const errorData = await res.json();
          console.error('🚀 Error response:', errorData);
          throw new Error('Error creating review');
        }

        const responseData: GetReviewResponseWithPermissions = await res.json();
        setActiveFilter(undefined);

        let currentReviewsData = reviewsData;
        if (!currentReviewsData) {
          currentReviewsData = await getReviews(1, activeFilter, activeSort);
        }

        if (responseData && currentReviewsData) {
          if (
            currentReviewsData.reviews?.some(
              (review) => review.reviewDetails.id === responseData.id
            )
          ) {
            return currentReviewsData;
          }

          const newReviewMapped = createOptimisticReview(
            responseData,
            productId,
            isMultiPackage
          );

          const optimisticReviewsData = {
            ...currentReviewsData,
            reviews: [newReviewMapped, ...(currentReviewsData.reviews ?? [])],
            start: 1,
            total: (currentReviewsData.total ?? 0) + 1,
            end: Math.min(
              (currentReviewsData.end ?? 0) + 1,
              (currentReviewsData.total ?? 0) + 1
            ),
          };

          setReviewsData(optimisticReviewsData);
          return optimisticReviewsData;
        }

        return null;
      } catch (error) {
        console.error('❌ Submit review failed:', error);
        throw error;
      }
    },
    [isMultiPackage, reviewsData, getReviews, activeFilter, activeSort]
  );

  const onDeleteReview = useCallback(
    async (reviewId: string) => {
      try {
        const url = new URL(
          `/api/products/${productId}/reviews/${reviewId}`,
          window.location.origin
        );

        const displayedData = reviewsData ?? initialReviewsData;
        const previousTotal = displayedData?.total;
        const previousReviews = displayedData?.reviews ?? [];
        const deletedWasDisplayed = previousReviews.some(
          (review) => review.reviewDetails.id === reviewId
        );

        const res = await fetch(url.toString(), { method: 'DELETE' });
        if (!res.ok) throw new Error('Error deleting review');

        const listsDeletedReview = (data: ReviewsData) =>
          Array.isArray(data.reviews) &&
          data.reviews.some(
            (review: ProductReviewsDataMapper) =>
              review.reviewDetails.id === reviewId
          );

        // The refetch only covers page 1, so a review deleted from a later page
        // cannot be detected by scanning it: fall back to comparing the total
        // against the state the deletion was issued from.
        const keepsPreviousTotal = (data: ReviewsData) =>
          previousTotal !== undefined &&
          data.total === previousTotal &&
          deletedWasDisplayed;

        // Local reconciliation can drop the deleted review but cannot pull in
        // the review that should shift up from the next page, so a stale read
        // is retried before falling back to it.
        let refreshedData: ReviewsData | undefined;
        for (let attempt = 0; attempt < DELETE_REFETCH_ATTEMPTS; attempt++) {
          if (attempt > 0) {
            await wait(DELETE_REFETCH_RETRY_DELAY_MS);
          }

          const data = await getReviews(1, activeFilter, activeSort);
          if (data) {
            refreshedData = data;
            if (!listsDeletedReview(data) && !keepsPreviousTotal(data)) {
              break;
            }
          }
        }

        // getReviews swallows its own failures, so a missing payload means the
        // list still shows the deleted review: surface it instead of letting
        // the caller treat the deletion as fully applied.
        if (!refreshedData) {
          throw new Error('Error refreshing reviews after delete');
        }

        if (!Array.isArray(refreshedData.reviews)) {
          return refreshedData;
        }

        const stillListed = listsDeletedReview(refreshedData);
        const deletedFromLaterPage = keepsPreviousTotal(refreshedData);

        if (!stillListed && !deletedFromLaterPage) {
          return refreshedData;
        }

        const reviews = refreshedData.reviews.filter(
          (review: ProductReviewsDataMapper) =>
            review.reviewDetails.id !== reviewId
        );
        // A stale list paired with an already-decremented total must not be
        // counted down twice.
        const isTotalStale =
          previousTotal === undefined || refreshedData.total === previousTotal;
        const total = isTotalStale
          ? Math.max(0, (refreshedData.total ?? 0) - 1)
          : (refreshedData.total ?? 0);
        const pageSize = refreshedData.count || PAGE_SIZE;

        const reconciledReviewsData = {
          ...refreshedData,
          reviews,
          total,
          start: reviews.length ? 1 : 0,
          end: reviews.length,
          currentPage: 1,
          totalPages: Math.max(1, Math.ceil(total / pageSize)),
        };

        setReviewsData(reconciledReviewsData);
        return reconciledReviewsData;
      } catch (error) {
        console.error('Delete review failed:', error);
        throw error;
      }
    },
    [
      productId,
      getReviews,
      activeFilter,
      activeSort,
      reviewsData,
      initialReviewsData,
    ]
  );

  const onEditReview = useCallback(
    async (
      reviewId: string,
      formData: ReviewFormData,
      lastUpdatedAt: string
    ) => {
      try {
        const newFormData = {
          ...formData,
          lastUpdatedAt,
        };
        const url = new URL(
          `/api/products/${productId}/reviews/${reviewId}`,
          window.location.origin
        );

        const res = await fetch(url.toString(), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newFormData),
        });

        if (!res.ok) throw new Error('Error editing review');

        const updatedReview: GetReviewResponse = await res.json();

        let currentReviewsData = reviewsData;
        if (!currentReviewsData) {
          currentReviewsData = await getReviews(1, activeFilter, activeSort);
        }

        if (
          updatedReview &&
          currentReviewsData &&
          Array.isArray(currentReviewsData.reviews)
        ) {
          const updatedReviews = currentReviewsData.reviews.map((review) => {
            if (review.reviewDetails.id === reviewId) {
              return updateOptimisticReview(
                review,
                updatedReview,
                productId,
                isMultiPackage
              );
            }
            return review;
          });

          const optimisticReviewsData = {
            ...currentReviewsData,
            reviews: updatedReviews,
          };

          setReviewsData(optimisticReviewsData);
          return optimisticReviewsData;
        }

        return currentReviewsData;
      } catch (error) {
        console.error('Edit review failed:', error);
        throw error;
      }
    },
    [
      productId,
      isMultiPackage,
      reviewsData,
      getReviews,
      activeFilter,
      activeSort,
    ]
  );

  const onEmailPublisher = useCallback(() => {
    if (
      !publisherSupportContact ||
      typeof publisherSupportContact !== 'string' ||
      publisherSupportContact.trim().length === 0
    ) {
      console.warn('Publisher support contact is not available or invalid.');
      return;
    }
    window.location.href = `mailto:${publisherSupportContact}`;
  }, [publisherSupportContact]);

  // TODO: These features are not going to be part of the GA launch
  const onHelpfulVotesReview = useCallback((reviewId: string) => {
    console.log('Helpful votes clicked for review:', reviewId);
  }, []);

  const onSubmitReply = useCallback(
    (reply: string, replyId?: string | undefined) => {
      console.log('Submit reply clicked:', reply, replyId);
    },
    []
  );

  const onHelpfulVotesReply = useCallback((replyId: string) => {
    console.log('Helpful votes clicked for reply:', replyId);
  }, []);

  const onEditReply = useCallback((replyId: string) => {
    console.log('Edit reply clicked for reply:', replyId);
  }, []);

  const onDeleteReply = useCallback((replyId: string) => {
    console.log('Delete reply clicked for reply:', replyId);
  }, []);

  const fetchReviewStats = useCallback(async (productId: string) => {
    try {
      const response = await fetch(`/api/products/${productId}/reviews/stats`);
      if (response.ok) {
        return await response.json();
      }
      throw new Error('Failed to fetch review stats');
    } catch (error) {
      console.error('Failed to fetch review stats:', error);
      throw error;
    }
  }, []);

  return {
    // Data
    reviewsData,
    getReviews,
    loading,
    error,
    activeFilter,
    activeSort,

    // Actions
    onFilter,
    onSort,
    onPageChange,
    onSubmitReport,
    onSubmitReview,
    onDeleteReview,
    onEditReview,
    onEmailPublisher,
    onHelpfulVotesReview,
    onSubmitReply,
    onHelpfulVotesReply,
    onEditReply,
    onDeleteReply,
    fetchReviewStats,
  };
};
